/**
 * Screen Wake Lock zolang er media op het toestel wacht.
 *
 * Waarom: uploaden gebeurt in het tabblad, en een telefoon met scherm uit
 * bevriest dat tabblad. Gemeten op 17-08: twee toestellen leverden hun
 * wachtrij af in burstjes van twee of drie foto's met minuten stilte ertussen,
 * en vielen uren volledig stil. Dat is geen netwerk (een toestel zonder
 * achterstand landt in 2 seconden) maar het scherm dat uitgaat.
 *
 * Dit is een pleister op het echte gat: uploaden hoort niet van een
 * openstaand scherm af te hangen. Zie plans/2026-08-17-media-background-sync.md
 * voor de structurele oplossing. Tot die er is houdt dit de wachtrij levend
 * zolang de wasser de app open heeft.
 *
 * Bewust alleen bij een NIET-lege wachtrij: een wake lock die altijd aan staat
 * is een lege accu, en dan gaat het toestel de kast in.
 */

type WakeLockSentinelAchtig = {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: 'release', cb: () => void) => void;
};

let sentinel: WakeLockSentinelAchtig | null = null;
let gewenst = false;

function beschikbaar(): boolean {
  return typeof navigator !== 'undefined' && 'wakeLock' in navigator;
}

async function pak(): Promise<void> {
  if (!beschikbaar() || sentinel || document.visibilityState !== 'visible') return;
  try {
    const wl = (navigator as Navigator & {
      wakeLock: { request: (type: 'screen') => Promise<WakeLockSentinelAchtig> };
    }).wakeLock;
    const s = await wl.request('screen');
    sentinel = s;
    // Het systeem mag de lock zelf intrekken (accu laag, gebruiker vergrendelt).
    // Dan moeten we hem opnieuw pakken zodra het tabblad weer zichtbaar is.
    s.addEventListener('release', () => {
      if (sentinel === s) sentinel = null;
    });
    console.log('[wakelock] scherm blijft aan tot de wachtrij leeg is');
  } catch (err) {
    // Weigering is geen fout: sommige toestellen staan het niet toe. De app
    // moet dan gewoon doorwerken.
    console.warn('[wakelock] aanvraag geweigerd:', err);
  }
}

async function los(): Promise<void> {
  const s = sentinel;
  sentinel = null;
  if (!s || s.released) return;
  try {
    await s.release();
    console.log('[wakelock] vrijgegeven, wachtrij is leeg');
  } catch {
    // niets te doen
  }
}

/**
 * Zet de gewenste toestand. Aanroepen zo vaak als je wilt; alleen een
 * daadwerkelijke wijziging raakt de lock aan.
 */
export function stelWakeLockIn(nodig: boolean): void {
  gewenst = nodig;
  if (nodig) void pak();
  else void los();
}

/**
 * Herstelt de lock als het tabblad terugkomt. Een wake lock overleeft het
 * wegvallen van zichtbaarheid niet, dus zonder dit is hij na één keer
 * wegklikken permanent weg terwijl de wachtrij nog vol staat.
 */
export function startWakeLockHerstel(): () => void {
  const bijZichtbaar = () => {
    if (document.visibilityState === 'visible' && gewenst) void pak();
  };
  document.addEventListener('visibilitychange', bijZichtbaar);
  return () => {
    document.removeEventListener('visibilitychange', bijZichtbaar);
    void los();
  };
}
